<template>
    <div @keydown.down.prevent.stop="goToNextOption" @keydown.up.prevent.stop="goToPreviousOption">
        <slot />
    </div>
</template>

<script lang="ts" setup>
    import { ref, getCurrentInstance } from 'vue';

    defineOptions({ compatConfig: { MODE: 3 } });

    const instance = getCurrentInstance();

    const focusedIndex = ref<number | null>(null);

    const getOptions = (): HTMLElement[] => {
        const el = instance?.proxy?.$el as HTMLElement;
        if (!el) return [];

        return [...el.querySelectorAll('[data-option="true"]')] as HTMLElement[];
    };

    const focus = () => {
        const options = getOptions();

        if (!options.length) {
            return;
        }

        const selectedElement = options.find((element) => {
            return element.dataset.selected === 'true';
        });

        focusElement(selectedElement || options[0]);
    };

    const focusElement = (element: HTMLElement, direction: 'up' | 'down' | null = 'down') => {
        const options = getOptions();
        const allDisabled = options.every(
            (option) => option.getAttribute('aria-disabled') === 'true'
        );
        let index = options.findIndex((option) => option.id === element.id);

        if (allDisabled) {
            return;
        }

        if (element.getAttribute('aria-disabled') === 'true') {
            if (direction === 'down') {
                index++;

                if (!options[index]) {
                    goToFirstOption('down');
                    return;
                }
            } else if (direction === 'up') {
                index--;

                if (!options[index]) {
                    goToLastOption('up');
                    return;
                }
            } else {
                return;
            }

            focusElement(options[index], direction);
            return;
        }

        element.scrollIntoView({ block: 'nearest' });
        element.focus();

        focusedIndex.value = index;
    };

    const goToPreviousOption = () => {
        const options = getOptions();

        if (focusedIndex.value === null) {
            focusElement(options[0], 'up');
            return;
        }

        if (focusedIndex.value <= 0) {
            goToLastOption('up');
            return;
        }

        focusElement(options[focusedIndex.value - 1], 'up');
    };

    const goToNextOption = () => {
        const options = getOptions();

        if (focusedIndex.value === null) {
            focusElement(options[0], 'down');
            return;
        }

        if (focusedIndex.value >= options.length - 1) {
            goToFirstOption('down');
            return;
        }

        focusElement(options[focusedIndex.value + 1], 'down');
    };

    const goToFirstOption = (direction: 'up' | 'down') => {
        focusElement(getOptions()[0], direction);
    };

    const goToLastOption = (direction: 'up' | 'down') => {
        const options = getOptions();
        focusElement(options.at(-1)!, direction);
    };

    defineExpose({
        focus,
        focusElement,
        focusedIndex,
        goToNextOption,
        goToPreviousOption,
        options: getOptions,
    });
</script>
